import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { ensureJpeg } from '../../../shared/utils/ensureJpeg';
import { ensureMp4H264Aac } from '../../../shared/utils/videoPipeline';
import { getOverlayForFilter, type FilterPresetId } from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';
import HighLogo from '../../components/HighLogo';
import logo from '../../../assets/logo.png';
import uploadViaBackendHls from '../../../shared/utils/uploadViaBackendHls';
import VideoPlayer from "../../../shared/components/VideoPlayer";
import HashtagSelector from '../../../shared/components/HashtagSelectorMobile';
import type { MediaItem } from '../../../shared/contexts/MediaPreviewContext';

type ExtMediaItem = MediaItem & {
  musicId?: string | null;
  videoId?: string | null;
  isHls?: boolean;
  thumbnailUrl?: string | null;
  posterUrl?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
}; 

type R2UploadResult =
  | { url: string }
  | {
      videoId: string;
      isHls: true;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
    };

interface SidebarData {
  hashtags?: string[];
}

interface PostLocation {
  city: string;
  country: string;
  countryCode?: string;
}

type Prefs = { analyticsConsent: boolean; pushConsent: boolean; locationConsent: boolean };

const fromServerMedia = (m: any, index: number): ExtMediaItem => {
  const filterId =
    m?.filterId ??
    m?.filter?.id ??
    m?.filter?.name ??
    null;

  const musicId =
    m?.musicId ??
    m?.music?._id ??
    m?.audio?._id ??
    m?.trackId ??
    null;

  return {
    id: `existing-${index}`,
    url: m?.url ?? null,
    videoId: m?.videoId ?? null,
    isHls: Boolean(m?.isHls),
    hlsUrl: m?.hlsUrl ?? m?.url ?? null,
    dashUrl: m?.dashUrl ?? null,
    posterUrl: m?.posterUrl ?? null,
    thumbnailUrl: m?.thumbnailUrl ?? m?.posterUrl ?? null,
    musicId,
    musicTitle:
      m?.musicTitle ??
      m?.songTitle ??
      m?.trackTitle ??
      m?.music?.title ??
      m?.audio?.title ??
      null,
    musicArtist:
      m?.musicArtist ??
      m?.songArtist ??
      m?.trackArtist ??
      m?.music?.artist ??
      m?.audio?.artist ??
      null,
    filterId,
  };
};


// read a media item's filter from context (or null)
const useFilterLookup = () => {
  const { mediaItems } = useMediaPreview();
  return (id: string) => mediaItems.find(m => m.id === id)?.filterId ?? null;
};

// build overlay style for a given filterId
const overlayStyleFor = (fid?: string | null): React.CSSProperties | null => {
  const id = (fid as FilterPresetId) || 'none';
  const { tint, opacity } = getOverlayForFilter(id);
  return tint ? { position: 'absolute', inset: 0, backgroundColor: tint, opacity, pointerEvents: 'none', borderRadius: 8 } : null;
};

const DAILY_POST_LIMIT = 20;
const MAX_POST_MEDIA_BYTES = Math.round(1.7 * 1024 * 1024 * 1024); // ~1.7 GB
const MAX_MEDIA_PER_POST = 8;
const MAX_HASHTAGS = 8; // 👈 NEW

// 3 minutes + 3 seconds (same as mobile HARD_CAP_SEC = 183)
const HARD_CAP_VIDEO_SEC = 183;
const MAX_VIDEO_DURATION_MS = HARD_CAP_VIDEO_SEC * 1000;
const MIN_VIDEO_DURATION_MS = 2.9 * 1000;

const GalleryPosting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();

  const currentPath = `${location.pathname}${location.search || ''}`;
  const { t } = useTranslation();
  const UNKNOWN = t("locationChange.unknown", "Unknown");

  const [isLoading, setIsLoading] = useState(false);

  const [postsLeft, setPostsLeft] = useState<number | null>(null);

  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
const [postBusy, setPostBusy] = useState(false);


  const [vpnBlockedMessage, setVpnBlockedMessage] = useState<string | null>(null);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
const [cancelBusy, setCancelBusy] = useState(false);


  const [isTranscoding, setIsTranscoding] = useState(false);
  const { mediaItems, setMediaItems, addOrReplaceMediaItem } = useMediaPreview();
  const { sidebarData }: { sidebarData?: SidebarData } = location.state || {};
  const { editTargetId, previewFilename, isPreview, previewFile } = location.state || {};
  const hasInjectedRef = useRef(false);
  const sidebarInitialized = useRef(false);
  const getFilterId = useFilterLookup();

 const postsLeftLabel =
  typeof postsLeft === 'number'
    ? ` (${postsLeft})`
    : '';



  const getApiBaseUrl = () => {
    const base = process.env.REACT_APP_API_BASE_URL;
    if (!base) {
      throw new Error("REACT_APP_API_BASE_URL is not defined");
    }
    // remove trailing /api if present, because you later add /api/...
    return base.replace(/\/api\/?$/, '');
  };
 
// in GalleryPosting.tsx (your prepareFileForUpload)
async function prepareFileForUpload(file: File): Promise<File> {
  if (file.type.startsWith('image/')) {
    return await ensureJpeg(file, 0.85);
  }

  if (file.type.startsWith('video/')) {
    try {
      const out = await ensureMp4H264Aac(file); // will await load() internally
      // normalize extension if needed
      if (out.type === 'video/mp4' && !/\.mp4$/i.test(out.name)) {
        const base = out.name.replace(/\.[^.]+$/, '');
        return new File([out], `${base}.mp4`, { type: 'video/mp4' });
      }
      return out;
    } catch (e) {
      // Soft fallback — just give it a .mp4-ish name; backend will transcode if needed
      const name = /\.mp4$/i.test(file.name)
        ? file.name
        : file.name.replace(/\.[^.]+$/, '') + '.mp4';
      return new File([file], name, { type: file.type || 'video/mp4' });
    }
  }

  return file;
}


const isVideoMedia = (item: any): boolean => {
  if (!item) return false;
  if (item.type === 'video') return true;
  if (item.isHls && item.videoId) return true;
  if (item.file?.type?.startsWith('video/')) return true;
  if (typeof item.url === 'string' && /\.(mp4|webm|mov|m4v)$/i.test(item.url)) return true;
  if (typeof item.hlsUrl === 'string' && item.hlsUrl.length > 0) return true;
  return false;
};

const toRemoteHttp = (item: any): string | undefined => {
 if (item.isHls) {
  return item.hlsUrl || item.url || (item.videoId
    ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
    : undefined);
}
  const u = item.url;
  if (!u) return undefined;
  if (u.startsWith("blob:")) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${getApiBaseUrl()}${u}`;
  return undefined;
};

const orderSingleVideoFirst = <T,>(items: T[], isVideo: (item: T) => boolean): T[] => {
  const video = items.find(isVideo);
  const rest = items.filter(item => !isVideo(item));
  return video ? [video, ...rest] : rest;
};

const hasAnyVideoInComposer = () => {
  return [...existingMedia, ...mediaItems].some(isVideoMedia);
};



useEffect(() => {
  const fetchPostsLeft = async () => {
    if (!user) {
      setPostsLeft(DAILY_POST_LIMIT);
      return;
    }

    if (!token) {
      setPostsLeft(DAILY_POST_LIMIT);
      return;
    }

    try {
      const { data } = await api.get('/media/my-posts');

      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const todayCount = (Array.isArray(data) ? data : []).filter((p: any) => {
        if (!p?.createdAt) return false;
        const created = new Date(p.createdAt);
        return created >= startOfDay;
      }).length;

      const remaining = Math.max(DAILY_POST_LIMIT - todayCount, 0);
      setPostsLeft(remaining);
    } catch (err) {
      console.error('Failed to fetch daily post count:', err);
      // Fallback: assume full allowance if error
      setPostsLeft(DAILY_POST_LIMIT);
    } finally {
    }
  };

  fetchPostsLeft();
}, [user, token]);


useEffect(() => {
  if (!sidebarData || sidebarInitialized.current) return;

  if (sidebarData.hashtags) {
    const limited = sidebarData.hashtags.slice(0, MAX_HASHTAGS); // 👈 clamp
    setSelectedHashtagList(limited);
  }

  sidebarInitialized.current = true; // ✅ only apply once
}, [sidebarData]);


useEffect(() => {
  if (!isPreview || !previewFilename || !previewFile) return;

  const previewId = editTargetId || `preview-${Date.now()}`;
  if (hasInjectedRef.current === previewFilename) return;

  const objectURL = URL.createObjectURL(previewFile);

  const existing = mediaItems.find(m => m.id === previewId);

  addOrReplaceMediaItem({
    id: previewId,
    file: previewFile,
    url: existing?.url,
    musicTitle: existing?.musicTitle ?? null,
    musicArtist: existing?.musicArtist ?? null,
    filterId: existing?.filterId ?? null, 
  });

  setObjectURLs((prev) => ({
    ...prev,
    [previewId]: objectURL,
  }));

  hasInjectedRef.current = previewFilename;

// ⛔️ no cleanup that revokes objectURL here
}, [isPreview, previewFilename, previewFile, editTargetId, mediaItems, addOrReplaceMediaItem]);
  const { postId } = location.state || {};
  const [existingMedia, setExistingMedia] = useState<ExtMediaItem[]>([]);
  const [objectURLs, setObjectURLs] = useState<Record<string, string>>({});
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [selectedHashtagList, setSelectedHashtagList] = useState<string[]>([]);

  const totalMediaCount = existingMedia.length + mediaItems.length;

const isPostDisabled =
  isLoading ||
  totalMediaCount === 0 ||
  postsLeft === 0;



  const canUseGeo = async () => {
    if (!('permissions' in navigator)) return true; // older browsers
    try {
      // @ts-ignore - older TS lib doms may not include 'geolocation'
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state !== 'denied'; // 'granted' or 'prompt' are OK
    } catch {
      return true;
    }
  };
  
  // Read video duration from a File (in ms), or null if unknown
const getVideoDurationMsFromFile = (file: File): Promise<number | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) {
      resolve(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const dur = video.duration; // seconds
      if (!isFinite(dur) || dur <= 0) {
        resolve(null);
      } else {
        resolve(Math.round(dur * 1000)); // ms
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    video.src = url;
  });
};

const [activeVideoChipId, setActiveVideoChipId] = useState<string | null>(null);
const chipHideTimeoutRef = useRef<number | null>(null);

const showMusicChipFor = (id: string) => {
  setActiveVideoChipId(id);

  if (chipHideTimeoutRef.current) {
    window.clearTimeout(chipHideTimeoutRef.current);
  }

  chipHideTimeoutRef.current = window.setTimeout(() => {
    setActiveVideoChipId(prev => (prev === id ? null : prev));
    chipHideTimeoutRef.current = null;
  }, 2500); // ≈ when native controls fade
};

useEffect(() => {
  return () => {
    if (chipHideTimeoutRef.current) {
      window.clearTimeout(chipHideTimeoutRef.current);
    }
  };
}, []);

  const fetchLocationForPosting = async (): Promise<PostLocation & { countryCode?: string }> => {
    if (!prefs?.locationConsent) {
      navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
      throw new Error('Location consent required');
    }
  
    // 👇 quick deny check — avoids waiting for getCurrentPosition
    const ok = await canUseGeo();
    if (!ok) {
      navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
      throw new Error('Browser geolocation denied');
    }
  
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
      });
      const { latitude, longitude } = position.coords;
      const lang = navigator.language || 'en';
  
      const res = await api.get('/media/reverse-geocode', {
        params: { lat: latitude, lng: longitude, lang },
      });
  
      const { city, country, countryCode } = res.data || {};
      return {
        city: city || UNKNOWN,
        country: country || UNKNOWN,
        countryCode,
      };
    } catch {
      return { city: UNKNOWN, country: UNKNOWN };
    }
  };
  
  const mediaLabel = (m?: Pick<MediaItem, 'musicTitle' | 'musicArtist'>) =>
    [m?.musicTitle, m?.musicArtist].filter(Boolean).join(' — ');
  
  //Fetch existing media data if postId is provided
  useEffect(() => {
    const fetchMediaData = async () => {
      if (!postId) return;
  
      try {
        const res = await api.get(`/events/${postId}`);
        // server may return strings or objects
        const rawList: any[] = Array.isArray(res.data?.media) ? res.data.media : [];
  
        const fetchedMedia = rawList
        .map(fromServerMedia)
        .filter(m => m.url || (m.isHls && m.videoId));

        setExistingMedia(fetchedMedia);
      } catch (err) {
        console.error("Failed to fetch media data:", err);
      }
    };
  
    fetchMediaData();
  }, [postId]);
  
  
  // Load privacy preferences
  useEffect(() => {
      (async () => {
        try {
          const { data } = await api.get('/privacy/preferences');
          setPrefs({
            analyticsConsent: !!data?.analyticsConsent,
            pushConsent: !!data?.pushConsent,
            locationConsent: !!data?.locationConsent,
          });
        } catch {
          setPrefs({ analyticsConsent: false, pushConsent: false, locationConsent: false });
        } finally {
          setPrefsLoading(false);
        }
      })();
    }, []);
  

 //Handle file selection
const uid = () =>
  (globalThis.crypto?.randomUUID?.() ??
    `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// ⬇️ new shared helper
const processPickedFiles = async (picked: File[]) => {
  if (picked.length === 0) return;

  const existingCount = existingMedia.length + mediaItems.length;
  let remainingSlots = Math.max(0, MAX_MEDIA_PER_POST - existingCount);

  if (remainingSlots <= 0) {
    alert(t('galleryPosting.alerts.mediaPerPostLimit'));
    return;
  }

  setIsTranscoding(true);

  const newItems: MediaItem[] = [];
  const newUrls: Record<string, string> = {};

  const existingNames = new Set(
    mediaItems.map(m => m.file?.name).filter(Boolean) as string[]
  );

  let skippedByDuration = 0;
  let skippedByCap = 0;
  let skippedByVideoRule = 0;

  let videoAlreadyExists = hasAnyVideoInComposer();
  let acceptedNewVideo = false;

for (const file of picked) {
  if (remainingSlots <= 0) {
    skippedByCap++;
    continue;
  }

  if (existingNames.has(file.name)) continue;

  const isVideoFile = file.type.startsWith('video/');

  // only one video total in the composer
  if (isVideoFile && (videoAlreadyExists || acceptedNewVideo)) {
    skippedByVideoRule++;
    continue;
  }

  if (isVideoFile) {
    const durMs = await getVideoDurationMsFromFile(file);

    if (durMs != null && durMs < MIN_VIDEO_DURATION_MS) {
      skippedByDuration++;
      continue;
    }

    if (durMs != null && durMs > MAX_VIDEO_DURATION_MS) {
      skippedByDuration++;
      continue;
    }
  }

  const id = `new-${uid()}`;
  newItems.push({ id, file } as MediaItem);
  newUrls[id] = URL.createObjectURL(file);
  remainingSlots--;

  if (isVideoFile) {
    acceptedNewVideo = true;
  }
}

  if (newItems.length === 0) {
    setIsTranscoding(false);

    if (skippedByDuration > 0) {
      alert(t('galleryPosting.alerts.videosSkippedTooLongShort'));
    }
    if (skippedByVideoRule > 0) {
      alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
    }
    if (skippedByCap > 0) {
      alert(t('galleryPosting.alerts.mediaPerPostHardLimit'));
    }

    return;
  }

  const orderedNewItems = orderSingleVideoFirst(
    newItems,
    (item) => Boolean(item.file?.type?.startsWith('video/'))
  );

  setMediaItems(prev => {
    const combined = [...prev, ...orderedNewItems];
    return orderSingleVideoFirst(combined, isVideoMedia);
  });

  setObjectURLs(prev => ({ ...prev, ...newUrls }));
  setIsTranscoding(false);

  if (skippedByDuration > 0) {
    alert(t('galleryPosting.alerts.videosSkippedTooLongSummary'));
  }
  if (skippedByVideoRule > 0) {
    alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
  }
  if (skippedByCap > 0) {
    alert(t('galleryPosting.alerts.filesSkippedByCapSummary'));
  }
};


const composerItems = orderSingleVideoFirst(
  [...existingMedia, ...mediaItems] as ExtMediaItem[],
  isVideoMedia
);

// ⬇️ existing input handler now just delegates
const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const picked = Array.from(e.target.files || []);
  await processPickedFiles(picked);
};

// ⬇️ NEW: drop handler (used by the drop zone)
const handleDropFiles = async (e: React.DragEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();

  if (isTranscoding) return;

  const picked = Array.from(e.dataTransfer.files || []);
  await processPickedFiles(picked);
};



  const handleDelete = (id: string) => {
    const updated = mediaItems.filter(item => item.id !== id);
    setMediaItems(updated);
  
    if (objectURLs[id]) {
      URL.revokeObjectURL(objectURLs[id]);
      setObjectURLs((prev) => {
        const updatedURLs = { ...prev };
        delete updatedURLs[id];
        return updatedURLs;
      });
    }
    };

    const handleOpenMusic = async (entry: {
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    }) => {
      // 1️⃣ Best case: we already know the track id
      if (entry.musicId) {
        navigate(`/musicdetails/${entry.musicId}`);
        return;
      }
    
      // 2️⃣ Fallback: search by EITHER title or artist (not "title artist")
      const q = (entry.musicTitle || entry.musicArtist || '').trim();
      if (!q) {
        return;
      }
    
      try {
        const res = await api.get('/music', { params: { q } });
        const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
    
        if (!list.length) {
          return;
        }
    
        navigate(`/musicdetails/${list[0]._id}`);
      } catch (e) {
        console.error('[GalleryPosting.handleOpenMusic] Music lookup failed:', e);
      }
    };
    
    
//Navigation hard loading to Gallery after upload
const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';

const goToGalleryHard = (replace = true) => {
  sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, '1');
  const url = `/gallery?refresh=${Date.now()}`;
  navigate(url, { replace });
};

//Posting to Gallery

  const postingRef = React.useRef(false);

  const handlePostToBlog = async () => {
    if (postingRef.current) {
      console.warn("🚧 Post action already in progress. Aborting...");
      return;
    } 
    if (!prefs || prefsLoading) {
            // Wait until we know; soft-block until prefs are loaded
            return;
          }
          if (!prefs.locationConsent) {
            navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
            return;
          }
    postingRef.current = true;
    setIsLoading(true); // Show loading overlay 
    try {
      if (!token) {
        alert(t('galleryPosting.alerts.loginRequired'));
        postingRef.current = false;
        setIsLoading(false); // Hide loading overlay
        return;
      }
  
      let detectedLocation: PostLocation = {
        city: UNKNOWN,
        country: UNKNOWN,
      };

      
      try {
        detectedLocation = await fetchLocationForPosting();
      } catch (err) {
      }
      await proceedWithPost(detectedLocation);
  
    } catch (err) {
      console.error("❌ Error in handlePostToBlog:", err instanceof Error ? err.message : err);
    } finally {
      postingRef.current = false;
      setIsLoading(false); // Hide loading overlay
    }
  }; 

  const confirmPost = async () => {
  if (postBusy) return;

  setPostBusy(true);
  try {
    await handlePostToBlog();
  } finally {
    setPostBusy(false);
    setPostConfirmOpen(false);
  }
};


  const proceedWithPost = async (place: PostLocation) => {
    try {
      if (!token) return;
  
      const itemsWithFiles = mediaItems.filter(mi => mi.file);
  
      const processedFiles = await Promise.all(
        itemsWithFiles.map(async mi => {
          const before = mi.file!;
          const after = await prepareFileForUpload(before);
          return after;
        })
      );
  
      const totalBytes = processedFiles.reduce(
        (sum, f) => sum + (f?.size || 0),
        0
      );
  
      if (totalBytes > MAX_POST_MEDIA_BYTES) {
        const actualGb = (totalBytes / (1024 * 1024 * 1024)).toFixed(2);
        const maxGb = (MAX_POST_MEDIA_BYTES / (1024 * 1024 * 1024)).toFixed(2);
        alert(
          t('galleryPosting.alerts.postTooLargeClient', {
            actualGb,
            maxGb,
          })
        );
        return;
      }
  
      const looksGenericCameraName = (base: string) => {
        const b = base.toLowerCase();
        return (
          b === 'media-0' || b === 'media-1' || b === 'media' ||
          b === 'video-0' || b === 'video' ||
          /^img[_-]?\d+$/i.test(base) ||
          /^vid[_-]?\d+$/i.test(base) ||
          /^pxl[_-]?\d+/i.test(base) ||
          base.length < 4
        );
      };
  
      const typePrefix = (f: File) =>
        f.type.startsWith('video/')
          ? 'video'
          : f.type.startsWith('image/')
          ? 'image'
          : 'file';
  
      const normalizeBaseFromFile = (f: File, fallbackId: string) => {
        const rawBase = (f.name || '').replace(/\.[^.]+$/, '');
        const safeRaw = rawBase.replace(/[^A-Za-z0-9._-]/g, '');
        if (!safeRaw || looksGenericCameraName(safeRaw)) {
          const stamp = new Date().toISOString().replace(/[:.]/g, '');
          return `${typePrefix(f)}_${stamp}`;
        }
        return safeRaw;
      };
  
      const extOf = (f: File) =>
        (f.name.match(/\.[a-z0-9]+$/i)?.[0]?.toLowerCase()) ||
        (f.type.startsWith('video/') ? '.mp4'
          : f.type.startsWith('image/') ? '.jpg'
          : '.bin');
  
      // ⬆️ Setup done — now handle uploads
      const r2Uploads: R2UploadResult[] = [];

// 1️⃣ First upload all VIDEOS (one by one)
for (let i = 0; i < processedFiles.length; i++) {
  const f = processedFiles[i];
  const srcItem = itemsWithFiles[i];

  if (!f.type.startsWith("video/")) {
  r2Uploads[i] = undefined as any;
    continue;
  }

  const ext = extOf(f);
  const base = normalizeBaseFromFile(f, srcItem.id);
  const stamp = new Date().toISOString().replace(/[:.]/g, "");
  const uniqueName = `${base}_${stamp}${ext}`;

  const fileWithUniqueName = new File([f], uniqueName, {
    type: f.type || "application/octet-stream",
  });

 const { videoId, hlsUrl, dashUrl, posterUrl } = await uploadViaBackendHls(fileWithUniqueName);

r2Uploads[i] = { videoId, isHls: true, hlsUrl, dashUrl, posterUrl};
}

// 2️⃣ Upload all IMAGES in parallel
const imagePromises = processedFiles.map(async (f, i) => {
  if (f.type.startsWith("video/")) return;

  const srcItem = itemsWithFiles[i];
  const ext = extOf(f);
  const base = normalizeBaseFromFile(f, srcItem.id);
  const stamp = new Date().toISOString().replace(/[:.]/g, "");
  const uniqueName = `${base}_${stamp}${ext}`;

  const fileWithUniqueName = new File([f], uniqueName, {
    type: f.type || "application/octet-stream",
  });

  const form = new FormData();
  form.append("file", fileWithUniqueName);

  const res = await api.post("/mediaImg/upload-image", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  r2Uploads[i] = { url: res.data.url };
});

await Promise.all(imagePromises);

      
      const uploadedUrlById = new Map<string, string>();

r2Uploads.forEach((u, i) => {
  const id = itemsWithFiles[i].id;
  if ("url" in u) {
    uploadedUrlById.set(id, u.url);
  }
});
 

      const uploadedVideoIdByItemId = new Map<string, string>();

r2Uploads.forEach((u, i) => {
  const id = itemsWithFiles[i].id;
  if ("videoId" in u) {
    uploadedVideoIdByItemId.set(id, u.videoId);
  }
});

  
      const metaFor = (id: string) => {
        const found = mediaItems.find(mi => mi.id === id);
        return {
          musicTitle: found?.musicTitle ?? null,
          musicArtist: found?.musicArtist ?? null,
          musicId: (found as any)?.musicId ?? null,
          filterId: found?.filterId ?? null,
        };
      };
  
      const existingEntries = existingMedia.map(m => {
        // 🎬 HLS
        if (m.isHls && m.videoId) {
  return {
    videoId: m.videoId,
    isHls: true,
    type: "video",
    hlsUrl: m.hlsUrl ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? m.thumbnailUrl ?? null,
    sizeBytes: null,
    musicTitle: m.musicTitle ?? null,
    musicArtist: m.musicArtist ?? null,
    musicId: m.musicId ?? null,
    filterId: m.filterId ?? null,
  };
}
      
        // 🖼 Image
        return {
          url: m.url!,
          type: "image",
          musicTitle: m.musicTitle ?? null,
          musicArtist: m.musicArtist ?? null,
          musicId: m.musicId ?? null,
          filterId: m.filterId ?? null,
        };
      });
      

      const uploadedEntries = itemsWithFiles.map((mi, idx) => {
  const file = processedFiles[idx];
  const isVideo = file?.type?.startsWith("video/");

if (isVideo) {
  const upload = r2Uploads[idx] as Extract<R2UploadResult, { videoId: string }>;
  const videoId = upload?.videoId;

  if (!videoId) {
    throw new Error(`Missing videoId for ${mi.id}`);
  }

  return {
    videoId,
    isHls: true,
    type: "video",
    hlsUrl: upload.hlsUrl ?? null,
    dashUrl: upload.dashUrl ?? null,
    posterUrl: upload.posterUrl ?? null,
    sizeBytes: file?.size ?? null,
    ...metaFor(mi.id),
  };
}

  // 🖼 IMAGE
  return {
    url: uploadedUrlById.get(mi.id)!,
    type: "image",
    sizeBytes: file?.size ?? null,
    mime: file?.type ?? null,
    isHls: false,
    ...metaFor(mi.id),
  };
});

        
  
   const mediaEntries = orderSingleVideoFirst(
     [...existingEntries, ...uploadedEntries],
       isVideoMedia
       );

const videoCount = mediaEntries.filter(isVideoMedia).length;

if (videoCount > 1) {
  alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
  return;
}

if (videoCount === 1 && !isVideoMedia(mediaEntries[0])) {
  alert(
    t(
      'galleryPosting.alerts.videoMustBeFirst',
      'Video must be the first media item.'
    )
  );
  return;
}

const limitedHashtags = selectedHashtagList.slice(0, MAX_HASHTAGS);

const mediaPost = {
  media: mediaEntries,
  location: place,
  hashtags: limitedHashtags.join(' '),
};

const postRes = await api.post('/media', mediaPost);

console.log('✅ Media post created:', postRes.data);

goToGalleryHard();


    } catch (err: any) {
      console.error('❌ Failed to upload media:', err);
  
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
  
      if (code === 'POST_TOO_LARGE') {
        const maxGb = (MAX_POST_MEDIA_BYTES / (1024 * 1024 * 1024)).toFixed(2);
        alert(
          t('galleryPosting.alerts.postTooLargeServer', {
            maxGb,
          })
        );
      } else if (code === 'DAILY_POST_LIMIT_REACHED') {
        alert(t('galleryPosting.alerts.dailyPostLimitReached'));
        setPostsLeft(0);
      } else if (status === 403 && code === 'VPN_BLOCKED') {
        const msg =
          err.response?.data?.msg ||
          t('galleryPosting.alerts.vpnBlockedFallback');
        alert(msg);
        setVpnBlockedMessage(msg);
      } else {
        alert(t('galleryPosting.alerts.postFailed'));
      }
    }
  };
  
  

  // Cancel: go back if possible; otherwise fall back to Gallery
const handleCancel = () => {
  setCancelConfirmOpen(true);
};

const confirmCancel = () => {
  setCancelBusy(true);

  if (window.history && window.history.length > 1) {
    navigate('/gallery');
  } else {
    navigate('/gallery', { replace: true });
  }
};

  const shouldReset = location.state?.reset === true;
  const hasReset = useRef(false);

  useEffect(() => {
  if (shouldReset && !hasReset.current) {
    hasReset.current = true;

    setMediaItems([]);
    setExistingMedia([]);
    setSelectedHashtagList([]);
    setObjectURLs({});
  }
}, [shouldReset, setMediaItems]);


useEffect(() => {
  if (!location.state?.postId && !location.state?.isPreview) {
    setMediaItems([]);
    setExistingMedia([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Playable source: prefer local blob URL during edit; else remote http(s)


// When building src, avoid returning "" (empty string can cause odd fetches)
const srcFor = (item: any): string | undefined => {
  const managed = objectURLs[item.id];
  if (managed) return managed;

  if (item.isHls) {
  return item.hlsUrl || item.url || (item.videoId
    ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
    : undefined);
}

  if (item.url?.startsWith("blob:")) return undefined;
  if (/^https?:\/\//i.test(item.url)) return item.url;
  if (item.url?.startsWith("/")) return `${getApiBaseUrl()}${item.url}`;

  return undefined;
};


// Keep a ref mirror to avoid using objectURLs in the effect dependency
const objectURLsRef = React.useRef(objectURLs);
React.useEffect(() => { objectURLsRef.current = objectURLs; }, [objectURLs]);

useEffect(() => {
  const toCreate: Record<string, string> = {};
  const existing = objectURLsRef.current;

  for (const item of mediaItems) {
    if (!item.file) continue;

    const isPreviewVideo =
      item.file.type.startsWith('video/') && item.url?.startsWith('/uploadsPreview/');
    const needsObjectUrl = !item.url || isPreviewVideo;

    if (!needsObjectUrl || existing[item.id] || toCreate[item.id]) continue;

    toCreate[item.id] = URL.createObjectURL(item.file);
  }

  if (Object.keys(toCreate).length) {
    setObjectURLs(prev => ({ ...prev, ...toCreate }));
  }
}, [mediaItems]); // 👈 not depending on objectURLs anymore



useEffect(() => {
  return () => {
    Object.values(objectURLsRef.current).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    });
  };
}, []);


  const LoadingOverlay = () => (
    <div style={loadingOverlayStyle}>
      <div style={loadingContentStyle}>
      <span>{t('galleryPosting.overlay.uploading')}</span>
        <div style={loadingDotsContainer}>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
        </div>
      </div>
    </div>
  );

  
  return (
    <>
      {isLoading && <LoadingOverlay />}

      <div style={pageShellStyle}>
        {/* HEADER – same visual family as Home / auth pages */}
        <header style={pageHeaderStyle}>
  <div style={pageHeaderRow}>
   <div
  style={pageHeaderLeft}
  onClick={() => navigate('/gallery')}
>
  <HighLogo
    src={logo}
    style={{ width: 100, display: 'block', marginLeft: '-20px', marginTop: '6px' }}
    padding={0}
  />
</div>

    {/* ✅ NEW TITLE */}
    <div style={pageTitleStyle}>
      📤 {t('galleryPostingSidebar.heading', 'Upload Your Post')}
    </div>
  </div>
</header>


        {/* MAIN CONTENT */}
        <main style={pageMainStyle}>
          {/* 🔔 VPN warning banner (if any) */}
          {vpnBlockedMessage && (
            <div style={vpnBannerStyle}>
              {vpnBlockedMessage}
            </div>
          )}

          {/* Main card with media area */}
          <div style={cardGridStyle}>

            {/* media card */}
            <section style={mediaOuterStyle}>
              <div style={postCardStyle}>
                {/* 👤 Header with Profile */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '5%',
                        marginRight: 10,
                        backgroundColor: user?.picture
                          ? 'transparent'
                          : 'rgb(38, 29, 162)',
                        backgroundImage: user?.picture
                          ? `url(${user.picture})`
                          : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: user?.picture ? 0 : '30px',
                      }}
                    >
                      {!user?.picture && '👻'}
                    </div>
                    <strong>
                      {user?.profilename || t('galleryPosting.fallback.anonymous')} 
                    </strong>
                  </div>
                </div>

                {/* ✅ Scrollable Media Section */}
                <div style={{ ...mediaScrollerStyle, marginTop: 5 }}>
{composerItems.map((item, index) => {
  
  const fid = getFilterId(item.id) ?? item.filterId ?? null;
  const overlay = overlayStyleFor(fid);
  const mediaUrl = item.isHls && item.videoId ? null : srcFor(item);

  return (
    <div
  key={item.id}
  style={{
    display: 'inline-block',     
    width: MEDIA_TILE_WIDTH,
    verticalAlign: 'top',
    marginRight: index === (composerItems.length - 1) ? 0 : MEDIA_GAP,

  }}
>

      {/* ================= VIDEO ================= */}
      {isVideoMedia(item) ? (
        <div
  onMouseMove={() => showMusicChipFor(item.id)}
  onMouseLeave={() => setActiveVideoChipId(null)}
  style={{
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  }}
>

          {/* ✅ HLS VIDEO */}
          {item.isHls && item.videoId ? (
            
    <VideoPlayer
  src={item.hlsUrl || item.url || undefined}
  poster={item.posterUrl || item.thumbnailUrl || undefined}
/>

) : (
<video
  src={mediaUrl ?? undefined}
  poster={item.thumbnailUrl || undefined}
  controls
  muted
  preload="metadata"
  playsInline

  onLoadedMetadata={(e) => {
    const v = e.currentTarget;
    try {
      v.currentTime = 0.001; // 👈 forces first frame preview
      v.pause();
    } catch {}
  }}

  style={{
    maxWidth: MEDIA_TILE_WIDTH,
    maxHeight: MEDIA_TILE_WIDTH,
    borderRadius: 12,
    backgroundColor: '#000',

    touchAction: 'pan-x',
    pointerEvents: 'auto',
  }}
/>
)}

{/* 🎵 MUSIC CHIP */}
{!!(item.musicTitle || item.musicArtist) && (
  <div
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -10,
      padding: '6px 10px 9px',
      fontSize: 12,
      color: '#c1c9d6',
      display: activeVideoChipId === item.id ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      background:
        'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.7) 100%)',
      pointerEvents: 'auto',
    }}
    title={mediaLabel(item)}
  >
    <span aria-hidden="true">🎵</span>

    {item.musicId ? (
      <Link
        to={`/musicdetails/${item.musicId}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          color: '#c1c9d6',
          fontWeight: 600,
          textDecoration: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {mediaLabel(item)}
      </Link>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenMusic(item);
        }}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          color: '#c1c9d6',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {mediaLabel(item)}
      </button>
    )}
  </div>
)}


          {overlay && (
            <div
              style={{
                ...overlay,
                borderRadius: 12,
                position: 'absolute',
                inset: 0,
              }}
            />
          )}
        </div>

      ) : (
      /* ================= IMAGE ================= */
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            src={mediaUrl ?? undefined}
            alt={`media-${index}`}
            loading="lazy"
            style={{
              maxWidth: MEDIA_TILE_WIDTH,
              maxHeight: MEDIA_TILE_WIDTH,
              objectFit: 'contain',
              borderRadius: 12,
            }}
          />

          {overlay && (
            <div
              style={{
                ...overlay,
                borderRadius: 12,
                position: 'absolute',
                inset: 0,
              }}
            />
          )}
        </div>
      )}

      {/* ================= ACTION BUTTONS ================= */}
      <div
        style={{
          width: '100%',
          maxWidth: MEDIA_TILE_WIDTH,
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          marginTop: 10,
        }}
      >
       {isVideoMedia(item) && (
  <button
    onClick={() => {
      navigate('/add-music', {
        state: {
          editTargetId: item.id,
          sidebarData: {
            hashtags: selectedHashtagList,
          },
         remoteUrl: item.isHls && item.videoId
  ? (item.hlsUrl || item.url || undefined)
  : srcFor(item),
 // fallback for existing videos
        },
      });
    }}
    style={{
      padding: '4px 8px',
      borderRadius: 999,
      background: '#2563eb',
      border: 'none',
      color: '#fff',
      fontSize: 12,
      cursor: 'pointer',
    }}
  >
    {item.musicId ? t('galleryPosting.addButtons.change', 'Change') : t('galleryPosting.addButtons.add', 'Add')} 🎵 
  </button>
)}

        <button
          onClick={() => handleDelete(item.id)}
          style={smallActionButton}
        >
          {t('galleryPosting.buttons.delete')}
        </button>


        <button
  onClick={(e) => {
    e.stopPropagation();

    // ensure latest filterId is persisted
    addOrReplaceMediaItem({
      id: item.id,
      url: item.url,
      file: item.file,
      musicTitle: item.musicTitle ?? null,
      musicArtist: item.musicArtist ?? null,
      filterId: getFilterId(item.id),
    });

    const remoteUrl = toRemoteHttp(item);

    if (isVideoMedia(item)) {
      navigate(`/editor/video/${item.id}`, {
        state: {
          editTargetId: item.id,
          mediaItem: item,
          remoteUrl,
          file: item.file ?? null, 
          sidebarData: {
            hashtags: selectedHashtagList,
          },
        },
      });
    } else {
      navigate(`/editor/image/${item.id}`, {
        state: {
          mediaItem: item,
          sidebarData: {
            hashtags: selectedHashtagList,
          },
        },
      });
    }
  }}
  style={smallActionButton}
>
  {t('galleryPosting.addButtons.filter')}
</button>

      </div>
    </div>
  );
})}


                  {/* ➕ Add Media tile */}
                  {totalMediaCount < MAX_MEDIA_PER_POST && (
                    <div
                      style={
                        totalMediaCount === 0
                          ? addMediaCardFullWidthStyle
                          : addMediaCardCompactStyle
                      }
                      onDragOver={(e) => {
                        if (!e.dataTransfer.types.includes('Files')) return;
                        e.preventDefault();
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget === e.target) {
                        }
                      }}
                      onDrop={handleDropFiles}
                    >
                      {isTranscoding ? (
                        <div className="spinner" />
                      ) : (
                        <label
                          style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 32, marginBottom: 6 }}>
                            📷 🎬
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              color: '#e5e7eb',
                              marginBottom: 4,
                            }}
                          >
                            {t("galleryPosting.addMedia.title", "Click here to choose Images & Videos")}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#9ca3af',
                            }}
                          >
                           {t("galleryPosting.addMedia.subtitle", "…or drop them here")}
                          </div>

                          <input
                            type="file"
                            accept="image/*, video/*"
                            multiple
                            onChange={handleFileChange}
                            disabled={isTranscoding}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
            {/* ✅ Bottom bar: Hashtags LEFT / Buttons RIGHT */}
<div style={bottomBarStyle}>
  {/* 🏷 Hashtags */}
  <div style={hashtagAreaStyle}>
   <div style={hashtagCompactWrapperStyle}>
  <HashtagSelector
    context="gallery"
    selectedTags={selectedHashtagList}
    onChange={(tags) => {
      const limited = tags.slice(0, MAX_HASHTAGS);
      setSelectedHashtagList(limited);
    }}
  />
</div>

  </div>

  {/* 🚀 Buttons */}
 <div style={bottomButtonGroupStyle}>
  <button
    onClick={() => setPostConfirmOpen(true)}
    disabled={isPostDisabled}
    style={{
      ...postButtonStyle,
      opacity: isPostDisabled ? 0.5 : 1,
      cursor: isPostDisabled ? 'not-allowed' : 'pointer',
    }}
  >
    🚀 {t('galleryPostingSidebar.buttons.post')}
    {postsLeftLabel}
  </button>

  <button
    onClick={handleCancel}
    style={cancelButtonStyle}
  >
    ❌ {t('galleryPostingSidebar.buttons.cancel')}
  </button>
</div>

</div>

          </div>
        </main>

        {postConfirmOpen && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 700 }}>
        🚀 {t('galleryPosting.confirmPublish.title', 'Publish this post?')}
      </div>

      <div style={{ marginBottom: 16, fontSize: 14, color: '#9ca3af' }}>
        {t(
          'galleryPosting.confirmPublish.subtitle',
          'This will upload your media and make it visible to others.'
        )}
      </div>

      <button
        onClick={confirmPost}
        disabled={postBusy}
        style={primaryBtnGreen}
      >
        {postBusy
          ? t('common.posting', 'Posting...')
          : t('common.confirm', 'Yes, publish')}
      </button>

      <button
        onClick={() => setPostConfirmOpen(false)}
        disabled={postBusy}
        style={cancelBtn}
      >
        {t('common.cancel', 'Cancel')}
      </button>
    </div>
  </div>
)}


        {cancelConfirmOpen && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
        {t('galleryPosting.alerts.discardConfirm')}
      </div>

      <button
        onClick={confirmCancel}
        disabled={cancelBusy}
        style={primaryBtn}
      >
        {t('common.confirm')}
      </button>

      <button
        onClick={() => setCancelConfirmOpen(false)}
        disabled={cancelBusy}
        style={cancelBtn}
      >
        {t('common.cancel')}
      </button>
    </div>
  </div>
)}

      </div>
    </>
  );

};

const MEDIA_GAP = 14;
const MEDIA_TILE_WIDTH = Math.min(window.innerWidth - 32, 520);


/* 🌌 Page shell */
const pageShellStyle: React.CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: 'flex',
  flexDirection: 'column',
};

/* 🧭 Header */
const pageHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0px',
  background: 'rgba(2, 6, 23, 0.1)',
  backdropFilter: 'blur(10px)',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};


/* 🧱 Main */
const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: '9px 0px 32px',
};

/* VPN banner */
const vpnBannerStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 10,
  backgroundColor: '#3b0d0d',
  color: '#ffcccc',
  fontSize: 14,
  marginBottom: 16,
};

/* Grid: sidebar + composer */
const cardGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

/* Composer area outer */
const mediaOuterStyle: React.CSSProperties = {
  width: '100%',
    flex: 1,
     marginLeft: 0,

};    

/* Composer card */
const postCardStyle: React.CSSProperties = {
  background: 'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(4, 7, 15, 0.95))',
  padding: '18px 0px 24px',
  borderRadius: 22,
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  color: '#e5e7eb',
  minHeight: 420,

};

/* Horizontal media scroller */
const mediaScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: MEDIA_GAP,

  overflowX: 'scroll',
  overflowY: 'hidden',

  WebkitOverflowScrolling: 'touch',

  width: '100%',
  maxWidth: '100%',

  paddingBottom: 16,
  paddingRight: 0,

  touchAction: 'pan-x',
};

/* Small action buttons under each media */
const smallActionButton: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#020617',
  border: '1px solid #cbd5f5',
  borderRadius: 999,
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
};


const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: '24px',
  color: '#380675',
};

const loadingDotsContainer: React.CSSProperties = {
  display: 'flex',
  gap: '5px',

};

const loadingDot: React.CSSProperties = {
  animation: 'loadingDots 1s infinite',
  fontSize: '36px',
};


/* ➕ Add media tile – base */
const addMediaBaseStyle: React.CSSProperties = {
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'center',
  border: '2px dashed rgba(148,163,184,0.6)',
  borderRadius: 20,
  background:
    'radial-gradient(circle at top, #020617, #020617 40%, #000 100%)',
};

/* Empty state → fills the container more */
const addMediaCardFullWidthStyle: React.CSSProperties = {
  ...addMediaBaseStyle,

  display: 'inline-flex',
  width: MEDIA_TILE_WIDTH,
  height: MEDIA_TILE_WIDTH,

  marginLeft: MEDIA_GAP,
  marginTop: 0,
};

/* Non-empty state → compact tile to the right */
const addMediaCardCompactStyle: React.CSSProperties = {
  ...addMediaBaseStyle,
  display: 'inline-flex',     
  width: MEDIA_TILE_WIDTH * 3,
  height: MEDIA_TILE_WIDTH * 0.95,
  marginTop: 0,
  marginRight: MEDIA_GAP,
};



const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "90%",
  maxWidth: 420,
  background: "#020617",
  borderRadius: 18,
  padding: 22,
  textAlign: "center",
  border: "1px solid rgba(148,163,184,0.35)",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#dc2626",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const primaryBtnGreen: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg,#22c55e,#4ade80)",
  color: "#0b1120",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};

const pageHeaderRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  marginTop: -8,
  marginLeft: '30px',
  letterSpacing: -0.01,
  color: '#e5e7eb',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};


const bottomBarStyle: React.CSSProperties = {
  marginTop: 0,
  paddingTop: 6,
  borderTop: '1px solid rgba(148,163,184,0.15)',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 14,

  height: 120,
  alignItems: 'flex-start', // ✅ top aligned
  overflow: 'hidden',
};

const hashtagAreaStyle: React.CSSProperties = {
  flex: 1,
  height: '100%',
  overflowY: 'auto',
  paddingRight: 6,
  paddingTop: 2,
};

const bottomButtonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column', // ✅ vertical stack
  gap: 10,
  alignItems: 'stretch',
  flexShrink: 0,
  minWidth: 140, // keeps buttons readable on mobile
};

const postButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  background: 'linear-gradient(135deg,#22c55e,#a3e635)',
  border: 'none',
  color: '#0b1120',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.4)',
  background: 'transparent',
  color: '#e5e7eb',
  fontSize: 14,
  cursor: 'pointer',
};

const hashtagCompactWrapperStyle: React.CSSProperties = {
  marginTop: 4,
  marginBottom: 4,
};

export default GalleryPosting;
